import React, { useRef, useState } from 'react';

import { DateTime } from 'luxon';

import styled from '@emotion/styled';

import { useQuery } from '@apollo/client/react';

import { FontWeight } from '@clutter/clean';

import {
  DoorToDoor__BundleEnum,
  GeocodeDocument,
  OrderCancellationPolicyGroupDocument,
  RateGroupKindEnum,
} from '@graphql/platform';

import {
  challengerPromoSummaryLabel,
  couponDiscountAmount,
  isAppointmentCoupon,
  isStorageCoupon,
} from '@root/components/checkout/cart/challenger_promo';
import { useReservationSummaryProps } from '@root/components/checkout/cart/use_reservation_summary_props';
import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { resolveAppointmentDateTime } from '@root/components/checkout/utilities/appointment_date';
import { PLANS } from '@root/constants/pricing';
import { WT_VISITOR_TOKEN } from '@root/initializers/wt';
import { Commitment } from '@root/resources/types/commitment';

import { formatCurrency } from '@utils/formatters';
import { PROTECTION_PLANS } from '@utils/protection_plan';

import { CancellationPolicyModal } from './cancellation_policy_modal';
import { DisposalSummaryRow } from './disposal_summary_row';
import { useChallengerPackingAmount } from './hooks/use_challenger_packing_amount';
import { useModalFocus } from './hooks/use_modal_focus';
import { CHALLENGER as T } from './tokens';

const DESKTOP = '@media (min-width: 840px)';
const MOBILE = '@media (max-width: 839px)';

const DISPOSAL_FROM_AMOUNT = 50;

/** Match desktop booking summary (Figma 29:3865). */
const COMMITMENT_LABEL: Record<Commitment, string> = {
  [RateGroupKindEnum.Mover]: 'Month to month',
  [RateGroupKindEnum.Flexer]: '4-month minimum',
  [RateGroupKindEnum.Saver]: '8-month minimum',
};

const Shell = styled.div`
  display: none;

  ${MOBILE} {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 30;
    flex-direction: column;
    align-items: center;
    background: ${T.color.stickySurface};
    border: 1px solid ${T.color.borderDefault};
    border-bottom: none;
    border-radius: ${T.radius.lg} ${T.radius.lg} 0 0;
    padding-top: ${T.space[8]};
    padding-bottom: env(safe-area-inset-bottom, 0);
    font-family: ${T.font.sans};
    box-sizing: border-box;
    cursor: pointer;
  }

  ${DESKTOP} {
    display: none;
  }
`;

const Handle = styled.div`
  width: 40px;
  height: 6px;
  border-radius: 999px;
  background: #c8d1d3;
  flex-shrink: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${T.space[16]};
  width: 100%;
  height: 84px;
  padding: ${T.space[8]} ${T.space[12]} ${T.space[16]} 2px;
  box-sizing: border-box;
`;

const Left = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  padding: 0 ${T.space[16]};
`;

const PriceLine = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  padding: 2px 0;
`;

const Price = styled.span`
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 22px;
  color: ${T.color.textSecondary};
`;

const PerMonth = styled.span`
  font-size: 13px;
  font-weight: ${FontWeight.Medium};
  line-height: 18px;
  color: ${T.color.textPrimary};
`;

/** Visual affordance only — whole Shell opens the fees drawer. */
const FeesLink = styled.span`
  display: inline-block;
  padding: 2px;
  align-self: flex-start;
  font-family: ${T.font.sans};
  font-size: 12px;
  font-weight: ${FontWeight.Semibold};
  line-height: 14px;
  color: ${T.color.linkBrand};
`;

const ReserveButton = styled.button<{ disabled?: boolean }>`
  appearance: none;
  width: 122px;
  height: 48px;
  flex-shrink: 0;
  border: none;
  border-radius: ${T.radius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  color: ${({ disabled }) =>
    disabled ? T.color.textDisabled : T.color.textInverse};
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const Backdrop = styled.button`
  appearance: none;
  border: none;
  padding: 0;
  position: fixed;
  inset: 0;
  z-index: 29;
  background: ${T.color.overlay};
  cursor: pointer;

  ${DESKTOP} {
    display: none;
  }
`;

/** Figma Mobile-08 booking summary sheet (707:20865). */
const FeesSheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 31;
  max-height: 85vh;
  overflow: auto;
  background: ${T.color.stickySurface};
  border: 1px solid ${T.color.borderDefault};
  border-bottom: none;
  border-radius: ${T.radius.lg} ${T.radius.lg} 0 0;
  padding: ${T.space[8]} ${T.space[16]} ${T.space[24]};
  font-family: ${T.font.sans};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${T.space[8]};

  ${DESKTOP} {
    display: none;
  }
`;

const SheetInner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  padding: ${T.space[12]} ${T.space[12]} ${T.space[16]};
  border-bottom: 1px solid ${T.color.borderDefault};
`;

const FeesTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 22px;
  color: ${T.color.textSecondary};
`;

const CostBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 ${T.space[12]};
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${T.space[8]};
  font-size: 13px;
  font-weight: ${FontWeight.Medium};
  line-height: 18px;
  color: ${T.color.textSecondary};
  padding: 2px 0;
`;

const Total = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 13px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  color: ${T.color.textSecondary};
`;

/** Match desktop booking summary CTA note (Funnel-Feedback 1:3421). */
const Note = styled.p`
  margin: 0;
  text-align: center;
  font-size: 12px;
  font-weight: ${FontWeight.Medium};
  line-height: 16px;
  color: ${T.color.textTertiary};
`;

const NoteLink = styled.button`
  appearance: none;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${T.color.linkBrand};
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
`;

const CtaBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${T.space[12]};
  width: 100%;
`;

const PromoLine = styled(Line)`
  color: ${T.color.textBrandDark};
`;

const SheetReserve = styled.button<{ disabled?: boolean }>`
  appearance: none;
  width: 100%;
  height: 48px;
  border: none;
  border-radius: ${T.radius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ disabled }) =>
    disabled ? T.color.bgButtonDisabled : T.color.textBrand};
  color: ${({ disabled }) =>
    disabled ? T.color.textDisabled : T.color.textInverse};
  font-family: ${T.font.sans};
  font-size: 16px;
  font-weight: ${FontWeight.Semibold};
  line-height: 16px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

type Props = {
  disabled: boolean;
  onReserve: () => void | Promise<void>;
};

export const MobileStickyBar: React.FC<Props> = ({ disabled, onReserve }) => {
  const [feesOpen, setFeesOpen] = useState(false);
  const feesSheetRef = useRef<HTMLDivElement>(null);
  useModalFocus(feesOpen, feesSheetRef);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const {
    pricingSummary,
    flowState: {
      values: {
        planSize,
        commitment,
        bundleKind,
        disposalSelected,
        dateScheduled,
        datePreferred,
        coupon,
        protectionPlan,
        zip,
      },
    },
  } = useStorageCheckoutContext();
  const summary = useReservationSummaryProps('challenger');

  const geocode =
    useQuery(GeocodeDocument, {
      variables: { zip: zip! },
      skip: !zip,
    }).data?.geocode ?? undefined;
  const { data: cancellationPolicyData } = useQuery(
    OrderCancellationPolicyGroupDocument,
    {
      variables: {
        visitorToken: WT_VISITOR_TOKEN,
        orderScheduledTime: dateScheduled?.fromTime,
      },
      skip: !geocode || geocode?.region?.name === 'Los Angeles',
    },
  );
  const cancelBy = cancellationPolicyData?.orderCancellationPolicyGroup
    ?.onboardingCancellationPolicy?.latestFreeCancellationDate
    ? DateTime.fromISO(
        cancellationPolicyData.orderCancellationPolicyGroup
          .onboardingCancellationPolicy.latestFreeCancellationDate,
      )
    : resolveAppointmentDateTime({ dateScheduled, datePreferred }).minus({
        days: 2,
      });

  const tax = (summary.storagePlanTax || 0) + (summary.protectionPlanTax || 0);
  const monthlyBaseAmount = summary.summaryLoaded
    ? summary.storagePlanAmount + summary.protectionPlanAmount
    : 0;
  const fullMonthlyTotal = summary.summaryLoaded
    ? summary.monthlyTotalAmount + tax
    : 0;

  const { amount: packingQuoteAmount, loading: packingQuoteLoading } =
    useChallengerPackingAmount();
  const packingAmount =
    bundleKind === DoorToDoor__BundleEnum.PickupAndPacking &&
    !packingQuoteLoading
      ? (packingQuoteAmount ?? 0)
      : 0;
  const packingLabel =
    bundleKind === DoorToDoor__BundleEnum.PickupAndPacking
      ? 'Packing assistance'
      : null;
  const scheduledArrivalAmount = dateScheduled?.feeAmount?.value ?? 0;
  const gasFeeAmount = pricingSummary?.gasFeeAmount ?? 0;
  const disposalAmount = disposalSelected ? DISPOSAL_FROM_AMOUNT : 0;
  const pickupFeesBeforePromo =
    packingAmount + scheduledArrivalAmount + gasFeeAmount + disposalAmount;

  const storagePromoDiscount =
    coupon && isStorageCoupon(coupon) && summary.summaryLoaded
      ? couponDiscountAmount(coupon, monthlyBaseAmount)
      : 0;
  const appointmentPromoDiscount =
    coupon && isAppointmentCoupon(coupon)
      ? couponDiscountAmount(coupon, pickupFeesBeforePromo)
      : 0;

  const monthlyTotal = Math.max(0, fullMonthlyTotal - storagePromoDiscount);
  const pickupTotal = Math.max(
    0,
    pickupFeesBeforePromo - appointmentPromoDiscount,
  );
  // Match desktop booking summary: no pick-up block until packing / disposal /
  // scheduled arrival / appointment promo (gas fee alone should not open it).
  const showPickup =
    packingAmount > 0 ||
    scheduledArrivalAmount > 0 ||
    disposalSelected ||
    appointmentPromoDiscount > 0;

  const protectionCoverage = protectionPlan
    ? PROTECTION_PLANS[protectionPlan].coverage
    : undefined;

  const handleReserve = () => {
    setFeesOpen(false);
    void onReserve();
  };

  return (
    <>
      {feesOpen && (
        <>
          <Backdrop
            type="button"
            aria-label="Close fees"
            onClick={() => setFeesOpen(false)}
          />
          <FeesSheet
            ref={feesSheetRef}
            role="dialog"
            aria-modal="true"
            aria-label="Booking summary"
          >
            <Handle aria-hidden />
            <SheetInner>
              <HeaderBlock>
                <FeesTitle>Booking summary</FeesTitle>
              </HeaderBlock>
              <CostBlock>
                <SectionLabel>Monthly storage plan</SectionLabel>
                {planSize ? (
                  <Line>
                    <span>
                      {PLANS[planSize].label} storage
                      {commitment && (
                        <>
                          <br />
                          {COMMITMENT_LABEL[commitment]}
                        </>
                      )}
                    </span>
                    <span>
                      {summary.summaryLoaded
                        ? formatCurrency(summary.storagePlanAmount)
                        : '—'}
                    </span>
                  </Line>
                ) : (
                  <Line>
                    <span>Storage plan</span>
                    <span>—</span>
                  </Line>
                )}
                {summary.protectionPlanAmount > 0 && protectionCoverage && (
                  <Line>
                    <span>{protectionCoverage} of protection</span>
                    <span>{formatCurrency(summary.protectionPlanAmount)}</span>
                  </Line>
                )}
                {coupon && storagePromoDiscount > 0 && (
                  <PromoLine>
                    <span>{challengerPromoSummaryLabel(coupon)}</span>
                    <span>-{formatCurrency(storagePromoDiscount)}</span>
                  </PromoLine>
                )}
                <Line>
                  <span>Tax</span>
                  <span>
                    {summary.summaryLoaded ? formatCurrency(tax) : '—'}
                  </span>
                </Line>
                <Total>
                  {summary.summaryLoaded ? formatCurrency(monthlyTotal) : '—'}
                </Total>
              </CostBlock>
              {showPickup && (
                <CostBlock>
                  <SectionLabel>Your initial pickup includes</SectionLabel>
                  {packingLabel && packingAmount > 0 && (
                    <Line>
                      <span>{packingLabel}</span>
                      <span>{formatCurrency(packingAmount)}</span>
                    </Line>
                  )}
                  {scheduledArrivalAmount > 0 && (
                    <Line>
                      <span>Specific arrival window</span>
                      <span>{formatCurrency(scheduledArrivalAmount)}</span>
                    </Line>
                  )}
                  {gasFeeAmount > 0 && (
                    <Line>
                      <span>Gas fee</span>
                      <span>{formatCurrency(gasFeeAmount)}</span>
                    </Line>
                  )}
                  {coupon && appointmentPromoDiscount > 0 && (
                    <PromoLine>
                      <span>{challengerPromoSummaryLabel(coupon)}</span>
                      <span>-{formatCurrency(appointmentPromoDiscount)}</span>
                    </PromoLine>
                  )}
                  {disposalSelected && (
                    <DisposalSummaryRow amount={disposalAmount} />
                  )}
                  <Total>{formatCurrency(pickupTotal)}</Total>
                </CostBlock>
              )}
              <CtaBlock>
                <SheetReserve
                  type="button"
                  disabled={disabled}
                  onClick={handleReserve}
                >
                  Reserve
                </SheetReserve>
                <Note>
                  You will be charged upon completion of your pickup. You have
                  until {cancelBy.toFormat('MMMM d')} to cancel. See
                  Clutter&apos;s{' '}
                  <NoteLink
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setShowCancellationModal(true);
                    }}
                  >
                    cancellation policy
                  </NoteLink>{' '}
                  for more details.
                </Note>
              </CtaBlock>
            </SheetInner>
          </FeesSheet>
        </>
      )}
      <Shell
        role="button"
        tabIndex={0}
        aria-expanded={feesOpen}
        aria-label="View booking summary details"
        onClick={() => setFeesOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setFeesOpen(true);
          }
        }}
      >
        <Handle aria-hidden />
        <Row>
          <Left>
            <PriceLine>
              <Price>
                {summary.summaryLoaded ? formatCurrency(monthlyTotal) : '—'}
              </Price>
              <PerMonth>per month</PerMonth>
            </PriceLine>
            <FeesLink>View all fees</FeesLink>
          </Left>
          <ReserveButton
            type="button"
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              void onReserve();
            }}
          >
            Reserve
          </ReserveButton>
        </Row>
      </Shell>
      <CancellationPolicyModal
        isOpen={showCancellationModal}
        onClose={() => setShowCancellationModal(false)}
      />
    </>
  );
};
